import Alert from '@amzn/awsui-components-core/alert';
import React from 'react';
import { MemberSubsetContainer } from 'src/components/star-grid/components/subset-editor/MemberSubsetContainer';
import { MemberSubsetFooter } from 'src/components/star-grid/components/subset-editor/MemberSubsetFooter';
import { MemberSubsetMemberList } from 'src/components/star-grid/components/subset-editor/MemberSubsetMemberList';
import { MemberSubsetToolbar } from 'src/components/star-grid/components/subset-editor/MemberSubsetToolbar';
import SubsetSaveAsDialog from 'src/components/star-grid/components/subset-editor/SubsetSaveAsDialog';
import { useMemberSubsetEditor } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetEditor';
import type { MemberSubsetModalProps } from 'src/components/star-grid/memberSubset.types';

/**
 * The member subset editor (TM1 subset-editor shape): one pane that IS the subset. The whole hierarchy
 * arrives in one fetch because the Keep/Leaves operations are set algebra over the tree.
 */
const MemberSubsetModal: React.FC<MemberSubsetModalProps> = (props) => {
  const {
    embedded,
    visible,
    onDismiss,
    dimensionLabel,
    hierarchies,
    toolbarLeading,
    testId,
    multiple = true,
    cascade = true,
    requireSelection = false
  } = props;
  const editor = useMemberSubsetEditor(props);
  const { subset, data, rows, actions } = editor;

  return (
    <MemberSubsetContainer
      embedded={embedded}
      visible={visible}
      onDismiss={onDismiss}
      // Only the standalone path; embedded hosts size their own Modal.
      size="x-large"
      header={`Select Elements - ${dimensionLabel}`}
      footer={
        <MemberSubsetFooter
          selectedMembers={rows.selectedMembers}
          selectedNotInView={rows.selectedNotInView}
          okDisabled={requireSelection && rows.selectedMembers !== 1}
          onCancel={onDismiss}
          onConfirm={editor.handleConfirm}
          testId={testId}
        />
      }
      data-testid={testId}
    >
      {data.error && (
        <Alert type="error" header="Could not load the hierarchy">
          {data.error}
        </Alert>
      )}
      {!data.error && (
        <MemberSubsetToolbar
          toolbarLeading={toolbarLeading}
          hierarchies={hierarchies}
          picked={data.picked}
          onHierarchyChange={editor.handleHierarchyChange}
          hasPair={data.aliasPair !== undefined}
          views={data.views}
          viewId={data.viewId}
          onViewChange={editor.handleViewChange}
          effectiveAlias={data.effectiveAlias}
          aliasNames={data.aliasNames}
          aliasNamesLoading={data.aliasNamesLoading}
          onAliasChange={data.setAlias}
          saveDisabled={rows.gridRows.length === 0}
          onSave={() => actions.setSaveMode('save')}
          onSaveAs={() => actions.setSaveMode('saveAs')}
          search={editor.search}
          onSearchChange={editor.setSearch}
          canUndo={subset.canUndo}
          canRedo={subset.canRedo}
          onUndo={subset.undo}
          onRedo={subset.redo}
          resetDisabled={editor.resetDisabled}
          onResetView={editor.handleResetView}
          onShowFlat={actions.showFlat}
          onShowAllMembers={actions.showAllMembers}
          onRemoveAll={actions.removeAllShown}
          selectionEmpty={rows.isUnrestricted}
          onKeep={actions.keepSelection}
          onExpandAll={actions.handleExpandAll}
          onCollapseAll={actions.handleCollapseAll}
          onRemoveElements={actions.removeElements}
          onRemoveWithDescendants={actions.removeWithDescendants}
          multiple={multiple}
          onSelectAll={actions.selectAll}
          onClearSelected={actions.clearSelected}
          testId={testId}
        />
      )}
      {/* Mounted only while open: a hidden Cloudscape Modal still leaves a dialog node in the DOM. */}
      {actions.saveMode !== null && (
        <SubsetSaveAsDialog
          visible
          rowCount={rows.gridRows.length}
          existing={editor.saveTarget}
          onDismiss={() => actions.setSaveMode(null)}
          onSave={actions.handleSaveAs}
          testId={testId ? `${testId}-save-as-dialog` : undefined}
        />
      )}
      {!data.error && (
        <MemberSubsetMemberList
          chain={rows.chain}
          loading={data.loading}
          viewLoading={data.viewLoading}
          aliasLabelsLoading={data.aliasLabelsLoading}
          isSearching={rows.isSearching}
          hasMembers={data.members.length > 0}
          gridKey={editor.gridKey}
          grid={{
            rows: rows.displayRows,
            // Forwarded, not defaulted: single-select hosts would otherwise cascade and never reach one member.
            multiple,
            cascade,
            theme: editor.agGridTheme,
            checked: subset.draft.checked,
            onCheckedChanged: subset.setChecked,
            onExpandedChanged: subset.setExpanded,
            initiallyExpanded: rows.initiallyExpanded,
            onRunOp: actions.runOp,
            onRemove: actions.handleRemove,
            onAddParents: actions.handleAddParents,
            onDrill: subset.drill,
            onApiReady: actions.handleApiReady
          }}
          testId={testId}
        />
      )}
    </MemberSubsetContainer>
  );
};

export default MemberSubsetModal;
